import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, Clock, ArrowRight, ChevronRight, X, SlidersHorizontal, Tag, Check, Sun, Moon } from "lucide-react";
import { blogArticles as staticArticles, type BlogCategory, type BlogArticle } from "@/data/blogArticles";
import blogHeroBgDark from "@/assets/blog-hero-bg.jpg";
import blogHeroBgLight from "@/assets/blog-hero-bg-light.jpg";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";

type SortKey = "newest" | "oldest" | "shortest" | "longest";
type Mode = "dark" | "light";

const PALETTES: Record<Mode, Record<string, string>> = {
  dark: {
    bg: "hsl(0 0% 4%)", surface: "hsl(0 0% 8%)", surfaceAlt: "hsl(0 0% 6%)", surfaceSoft: "hsl(0 0% 7%)",
    chipBg: "hsl(0 0% 12%)", kbdBg: "hsl(0 0% 10%)",
    border: "hsl(0 0% 16%)", borderSoft: "hsl(0 0% 14%)", borderStrong: "hsl(0 0% 20%)", borderKbd: "hsl(0 0% 22%)",
    divider: "hsl(0 0% 25%)",
    text: "hsl(0 0% 95%)", textStrong: "hsl(0 0% 92%)", textStrong2: "hsl(0 0% 90%)",
    text85: "hsl(0 0% 85%)", text80: "hsl(0 0% 80%)", text70: "hsl(0 0% 70%)", text68: "hsl(0 0% 68%)",
    text65: "hsl(0 0% 65%)", text60: "hsl(0 0% 60%)", text55: "hsl(0 0% 55%)", text50: "hsl(0 0% 50%)",
    text45: "hsl(0 0% 45%)", text40: "hsl(0 0% 40%)",
    hoverBg: "rgba(255,255,255,0.05)", hoverText: "hsl(0 0% 100%)",
  },
  light: {
    bg: "hsl(40 30% 97%)", surface: "hsl(0 0% 100%)", surfaceAlt: "hsl(40 25% 95%)", surfaceSoft: "hsl(40 25% 96%)",
    chipBg: "hsl(220 14% 94%)", kbdBg: "hsl(220 14% 94%)",
    border: "hsl(220 15% 86%)", borderSoft: "hsl(220 15% 90%)", borderStrong: "hsl(220 15% 78%)", borderKbd: "hsl(220 15% 80%)",
    divider: "hsl(220 10% 70%)",
    text: "hsl(220 25% 12%)", textStrong: "hsl(220 25% 15%)", textStrong2: "hsl(220 25% 18%)",
    text85: "hsl(220 20% 22%)", text80: "hsl(220 18% 26%)", text70: "hsl(220 14% 34%)", text68: "hsl(220 14% 36%)",
    text65: "hsl(220 14% 40%)", text60: "hsl(220 12% 44%)", text55: "hsl(220 12% 48%)", text50: "hsl(220 10% 52%)",
    text45: "hsl(220 10% 56%)", text40: "hsl(220 10% 60%)",
    hoverBg: "rgba(0,0,0,0.04)", hoverText: "hsl(218 60% 14%)",
  },
};

// Category color accents (hue values) for visual differentiation
const CATEGORY_HUE: Record<string, number> = {
  "Lead Generation": 207,
  "Outsourcing": 160,
  "Cold Calling": 28,
  "Sales Strategy": 340,
  "Digital Marketing": 270,
  "Business Growth": 142,
  "AI for Business": 190,
};

const PAGE_SIZE = 9;
const accent = (cat: string) => CATEGORY_HUE[cat] ?? 207;

const normalizeTag = (t: string) =>
  t.trim().toLowerCase().replace(/\s+/g, " ");

const Blog = () => {
  const [search, setSearch] = useState("");
  const [activeCategories, setActiveCategories] = useState<Set<string>>(new Set());
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>("newest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [showAllTags, setShowAllTags] = useState(false);
  const [dbArticles, setDbArticles] = useState<BlogArticle[]>([]);
  const [loaded, setLoaded] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<Mode>(() => {
    if (typeof window === "undefined") return "dark";
    return (localStorage.getItem("blog-read-mode") as Mode) || "dark";
  });
  useEffect(() => { localStorage.setItem("blog-read-mode", mode); }, [mode]);
  const c = PALETTES[mode];
  const isDark = mode === "dark";

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await supabase
        .from("blog_articles")
        .select("*")
        .eq("published", true)
        .order("date", { ascending: false });
      if (data && data.length > 0) {
        setDbArticles(data.map((d: any) => ({
          slug: d.slug, title: d.title, excerpt: d.excerpt, summary: d.summary,
          category: d.category as BlogCategory, author: d.author, authorRole: d.author_role,
          date: d.date, readingTime: d.reading_time, featured: d.featured,
          content: d.content || [], keyTakeaways: d.key_takeaways || [],
          faqs: d.faqs || [], relatedSlugs: d.related_slugs || [],
          seoTitle: d.seo_title, metaDescription: d.meta_description,
          tags: Array.isArray(d.target_keywords) ? d.target_keywords : [],
        })));
      }
      setLoaded(true);
    };
    fetchData();
  }, []);

  // Keyboard shortcut: "/" focuses search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Reset pagination when filters change
  useEffect(() => { setVisible(PAGE_SIZE); }, [search, activeCategories, activeTags, sortKey]);

  const allArticles = useMemo(() => {
    if (!loaded) return staticArticles;
    const dbSlugs = new Set(dbArticles.map((a) => a.slug));
    return [...dbArticles, ...staticArticles.filter((a) => !dbSlugs.has(a.slug))];
  }, [dbArticles, loaded]);

  // Counts per category
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allArticles.forEach((a) => { counts[a.category] = (counts[a.category] || 0) + 1; });
    return counts;
  }, [allArticles]);

  const categories = useMemo(
    () => Array.from(new Set(allArticles.map((a) => a.category))),
    [allArticles]
  );

  // Collect all tags + counts, sorted by frequency
  const tagData = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    allArticles.forEach((a) => {
      (a.tags || []).forEach((raw) => {
        const key = normalizeTag(raw);
        if (!key) return;
        const existing = counts.get(key);
        if (existing) existing.count += 1;
        else counts.set(key, { label: raw.trim(), count: 1 });
      });
    });
    return Array.from(counts.entries())
      .map(([key, v]) => ({ key, label: v.label, count: v.count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [allArticles]);

  const featured = allArticles.find((a) => a.featured);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = allArticles.filter((a) => {
      const matchesSearch =
        !q ||
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q) ||
        (a.tags || []).some((t) => t.toLowerCase().includes(q));

      // OR within categories: empty set = all
      const matchesCategory =
        activeCategories.size === 0 || activeCategories.has(a.category);

      // AND across tags: article must contain every selected tag
      const articleTagKeys = new Set((a.tags || []).map(normalizeTag));
      const matchesTags =
        activeTags.size === 0 ||
        Array.from(activeTags).every((t) => articleTagKeys.has(t));

      const noFilters = activeCategories.size === 0 && activeTags.size === 0 && !q;
      // In default unfiltered view, exclude the hero/featured article (it's shown above)
      return matchesSearch && matchesCategory && matchesTags && (!noFilters || !a.featured);
    });

    return [...list].sort((a, b) => {
      switch (sortKey) {
        case "oldest": return +new Date(a.date) - +new Date(b.date);
        case "shortest": return a.readingTime - b.readingTime;
        case "longest": return b.readingTime - a.readingTime;
        case "newest":
        default: return +new Date(b.date) - +new Date(a.date);
      }
    });
  }, [search, activeCategories, activeTags, sortKey, allArticles]);

  const visibleArticles = filtered.slice(0, visible);
  const hasFilters = !!search || activeCategories.size > 0 || activeTags.size > 0 || sortKey !== "newest";
  const clearFilters = () => {
    setSearch("");
    setActiveCategories(new Set());
    setActiveTags(new Set());
    setSortKey("newest");
  };

  const toggleCategory = (cat: string) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const toggleTag = (key: string) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const showHero = featured && activeCategories.size === 0 && activeTags.size === 0 && !search;
  const TAG_PREVIEW = 12;
  const tagsToShow = showAllTags ? tagData : tagData.slice(0, TAG_PREVIEW);

  return (
    <>
      <Helmet>
        <title>Elevate Insights , Blog | Elevate Global Solutions</title>
        <meta name="description" content="Strategies, ideas, and insights on outsourcing, lead generation, digital marketing, and business growth from Elevate Global Solutions." />
        <meta property="og:title" content="Elevate Insights , Blog | Elevate Global" />
        <meta property="og:description" content="Strategies, ideas, and insights to help businesses grow smarter." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.elevateglobalsolutions.net/blog" />
        <meta property="og:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:title" content="Elevate Insights , Blog | Elevate Global" />
        <meta name="twitter:description" content="Strategies, ideas, and insights to help businesses grow smarter." />
        <link rel="canonical" href="https://www.elevateglobalsolutions.net/blog" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Elevate Insights",
          description: "Strategies, ideas, and insights on outsourcing, lead generation, and business growth.",
          url: "https://www.elevateglobalsolutions.net/blog",
          publisher: { "@id": "https://www.elevateglobalsolutions.net/#organization" },
          blogPost: allArticles.slice(0, 20).map((a) => ({
            "@type": "BlogPosting",
            headline: a.title,
            url: `https://www.elevateglobalsolutions.net/blog/${a.slug}`,
            datePublished: a.date,
            author: { "@type": "Organization", name: a.author },
          })),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.elevateglobalsolutions.net/" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.elevateglobalsolutions.net/blog" },
          ],
        })}</script>
      </Helmet>

      <div className={`${isDark ? "blog-dark" : "blog-light"} min-h-screen transition-colors`} style={{ background: c.bg }}>
        {/* Theme toggle , floating top-right */}
        <button
          onClick={() => setMode(isDark ? "light" : "dark")}
          aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
          title={`Switch to ${isDark ? "light" : "dark"} mode`}
          className="fixed right-4 top-20 z-40 flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-medium shadow-lg backdrop-blur transition-all hover:scale-105"
          style={{ background: c.surface, borderColor: c.border, color: c.text80 }}
        >
          {isDark ? <Sun size={14} /> : <Moon size={14} />}
          <span className="hidden sm:inline">{isDark ? "Light" : "Dark"}</span>
        </button>

        {/* Hero */}
        <section
          className="group/hero relative overflow-hidden pb-10 pt-20 md:pb-14 md:pt-28"
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const x = ((e.clientX - r.left) / r.width - 0.5) * 2;
            const y = ((e.clientY - r.top) / r.height - 0.5) * 2;
            e.currentTarget.style.setProperty("--bx", `${x * 12}px`);
            e.currentTarget.style.setProperty("--by", `${y * 12}px`);
            e.currentTarget.style.setProperty("--gx", `${50 + x * 18}%`);
            e.currentTarget.style.setProperty("--gy", `${20 + y * 18}%`);
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.setProperty("--bx", `0px`);
            e.currentTarget.style.setProperty("--by", `0px`);
            e.currentTarget.style.setProperty("--gx", `50%`);
            e.currentTarget.style.setProperty("--gy", `20%`);
          }}
        >
          {/* Background photo (theme-aware, parallax on hover) */}
          <div
            className="pointer-events-none absolute inset-0 bg-cover bg-center transition-[transform,opacity] duration-500 ease-out will-change-transform group-hover/hero:scale-[1.04]"
            style={{
              backgroundImage: `url(${isDark ? blogHeroBgDark : blogHeroBgLight})`,
              opacity: isDark ? 0.22 : 0.55,
              transform: "translate3d(var(--bx, 0px), var(--by, 0px), 0)",
            }}
            aria-hidden="true"
          />
          {/* Readability gradient overlay (theme-aware) */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background: isDark
                ? `linear-gradient(180deg, ${c.bg} 0%, hsl(0 0% 4% / 0.55) 40%, ${c.bg} 100%)`
                : `linear-gradient(180deg, hsl(40 30% 97% / 0.45) 0%, hsl(40 30% 97% / 0.7) 60%, ${c.bg} 100%)`,
            }}
            aria-hidden="true"
          />
          {/* Interactive spotlight following the cursor */}
          <div
            className="pointer-events-none absolute inset-0 transition-opacity duration-300"
            style={{
              background: isDark
                ? `radial-gradient(circle at var(--gx, 50%) var(--gy, 20%), hsl(42 55% 55% / 0.18), transparent 55%)`
                : `radial-gradient(circle at var(--gx, 50%) var(--gy, 20%), hsl(42 80% 55% / 0.22), transparent 55%)`,
            }}
            aria-hidden="true"
          />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(207_68%_50%/0.10),transparent_60%)]" />
          <div className="container-narrow relative z-10 px-4 md:px-8 lg:px-16">
            <nav aria-label="Breadcrumb" className="mb-8">
              <ol className="flex items-center gap-1.5 text-sm" style={{ color: c.text70 }}>
                <li><Link to="/" className="transition-colors" style={{ color: c.text70 }} onMouseEnter={(e) => (e.currentTarget.style.color = c.hoverText)} onMouseLeave={(e) => (e.currentTarget.style.color = c.text70)}>Home</Link></li>
                <li><ChevronRight size={14} /></li>
                <li style={{ color: c.text80 }}>Blog</li>
              </ol>
            </nav>


            <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl" style={{ color: c.text }}>
              Elevate Insights
            </h1>
            <p className="mb-2 text-lg md:text-xl" style={{ color: c.text70 }}>
              Strategies, ideas, and insights to help businesses grow smarter.
            </p>
            <p className="mb-8 max-w-2xl text-sm md:text-base" style={{ color: c.text70 }}>
              Learn about outsourcing, lead generation, digital marketing, remote teams, and business growth.
            </p>

            {/* Search */}
            <div className="relative max-w-xl">
              <label htmlFor="blog-search" className="sr-only">Search articles</label>
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: c.text65 }} aria-hidden="true" />
              <input
                ref={searchRef}
                id="blog-search"
                type="text"
                placeholder="Search articles, categories, tags..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search articles"
                className="w-full rounded-lg border py-2.5 pl-10 pr-20 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[hsl(207_68%_50%/0.4)]"
                style={{ background: c.surface, borderColor: c.border, color: c.textStrong2 }}
              />
              {search ? (
                <button onClick={() => setSearch("")} aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 transition-colors hover:bg-white/10"
                  style={{ color: c.text70 }}>
                  <X size={16} />
                </button>
              ) : (
                <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border px-1.5 py-0.5 text-[10px] font-mono md:inline-block"
                  style={{ borderColor: c.borderKbd, background: c.kbdBg, color: c.text60 }} aria-hidden="true">
                  /
                </kbd>
              )}
            </div>
          </div>
        </section>

        {/* Featured Article */}
        {showHero && (
          <section className="container-narrow px-4 pb-12 md:px-8 lg:px-16">
            <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-widest" style={{ color: c.text50 }}>
              <span className="h-px w-8" style={{ background: c.divider }} />
              Editor's Pick
            </div>
            <Link to={`/blog/${featured!.slug}`}
              className="group relative block overflow-hidden rounded-2xl border transition-all duration-300 hover:border-[hsl(207_68%_50%/0.5)]"
              style={{ background: `linear-gradient(135deg, ${c.surface}, ${c.surfaceAlt})`, borderColor: c.border }}>
              <div className="pointer-events-none absolute inset-0 opacity-40 transition-opacity group-hover:opacity-60"
                style={{ background: `radial-gradient(ellipse at top right, hsl(${accent(featured!.category)} 68% 50% / 0.18), transparent 60%)` }} />
              <div className="relative p-6 md:p-10">
                <span className="mb-4 inline-block rounded-full px-3 py-1 text-xs font-medium"
                  style={{ background: `hsl(${accent(featured!.category)} 68% 50% / 0.18)`, color: `hsl(${accent(featured!.category)} 68% 72%)` }}>
                  {featured!.category}
                </span>
                <h2 className="mb-3 text-2xl font-bold leading-tight md:text-3xl lg:text-4xl" style={{ color: c.text }}>
                  {featured!.title}
                </h2>
                <p className="mb-6 max-w-2xl text-base" style={{ color: c.text65 }}>{featured!.excerpt}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm" style={{ color: c.text68 }}>
                  <span>{featured!.author}</span>
                  <span>·</span>
                  <span>{new Date(featured!.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1"><Clock size={14} /> {featured!.readingTime} min read</span>
                </div>
                <div className="mt-6 flex items-center gap-2 text-sm font-medium transition-all group-hover:gap-3" style={{ color: `hsl(${accent(featured!.category)} 68% 65%)` }}>
                  Read Article <ArrowRight size={16} />
                </div>
              </div>
            </Link>
          </section>
        )}

        {/* Categories + Sort */}
        <section className="container-narrow px-4 pb-4 md:px-8 lg:px-16">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest" style={{ color: c.text50 }}>
              Categories {activeCategories.size > 0 && <span style={{ color: "hsl(207 68% 65%)" }}>· {activeCategories.size} selected</span>}
            </p>
            <div className="flex items-center gap-2">
              <SlidersHorizontal size={14} style={{ color: c.text60 }} />
              <label htmlFor="blog-sort" className="text-xs" style={{ color: c.text60 }}>Sort</label>
              <select id="blog-sort" value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}
                className="rounded-md border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(207_68%_50%/0.4)]"
                style={{ background: c.surface, borderColor: c.border, color: c.text85 }}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="shortest">Quickest read</option>
                <option value="longest">Longest read</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const isActive = activeCategories.has(cat);
              const hue = accent(cat);
              return (
                <button key={cat} onClick={() => toggleCategory(cat)}
                  aria-pressed={isActive}
                  className="group flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-all"
                  style={{
                    background: isActive ? `hsl(${hue} 68% 50%)` : c.surface,
                    color: isActive ? "white" : c.text70,
                    border: `1px solid ${isActive ? `hsl(${hue} 68% 50%)` : c.border}`,
                  }}>
                  {isActive && <Check size={12} aria-hidden="true" />}
                  <span>{cat}</span>
                  <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums"
                    style={{
                      background: isActive ? "rgba(255,255,255,0.22)" : c.borderSoft,
                      color: isActive ? "white" : c.text60,
                    }}>
                    {categoryCounts[cat] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Tags */}
        {tagData.length > 0 && (
          <section className="container-narrow px-4 pb-4 md:px-8 lg:px-16">
            <div className="mb-2 flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest" style={{ color: c.text50 }}>
                <Tag size={12} />
                Tags
                {activeTags.size > 0 && <span style={{ color: "hsl(207 68% 65%)" }}>· {activeTags.size} selected (match all)</span>}
              </p>
              {tagData.length > TAG_PREVIEW && (
                <button onClick={() => setShowAllTags((v) => !v)}
                  className="text-xs underline-offset-2 hover:underline" style={{ color: c.text65 }}>
                  {showAllTags ? "Show fewer" : `Show all (${tagData.length})`}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tagsToShow.map((tag) => {
                const isActive = activeTags.has(tag.key);
                return (
                  <button key={tag.key} onClick={() => toggleTag(tag.key)}
                    aria-pressed={isActive}
                    className="flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-all"
                    style={{
                      background: isActive ? "hsl(207 68% 50% / 0.18)" : c.surfaceSoft,
                      color: isActive ? "hsl(207 68% 78%)" : c.text68,
                      border: `1px solid ${isActive ? "hsl(207 68% 50% / 0.5)" : c.borderSoft}`,
                    }}>
                    {isActive && <Check size={10} aria-hidden="true" />}
                    <span>{tag.label}</span>
                    <span className="text-[10px] tabular-nums" style={{ color: isActive ? "hsl(207 68% 65%)" : c.text45 }}>
                      {tag.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Active filter chips + Results bar */}
        <section className="container-narrow px-4 pb-4 md:px-8 lg:px-16">
          {(activeCategories.size > 0 || activeTags.size > 0) && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="text-xs" style={{ color: c.text55 }}>Active:</span>
              {Array.from(activeCategories).map((cat) => {
                const hue = accent(cat);
                return (
                  <button key={`c-${cat}`} onClick={() => toggleCategory(cat)}
                    className="flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs"
                    style={{ background: `hsl(${hue} 68% 50% / 0.18)`, color: `hsl(${hue} 68% 75%)`, border: `1px solid hsl(${hue} 68% 50% / 0.4)` }}>
                    {cat} <X size={10} />
                  </button>
                );
              })}
              {Array.from(activeTags).map((key) => {
                const meta = tagData.find((t) => t.key === key);
                return (
                  <button key={`t-${key}`} onClick={() => toggleTag(key)}
                    className="flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs"
                    style={{ background: c.chipBg, color: c.text80, border: `1px solid ${c.borderKbd}` }}>
                    #{meta?.label ?? key} <X size={10} />
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center justify-between text-sm" style={{ color: c.text60 }}>
            <p>
              Showing <span style={{ color: c.textStrong2 }}>{visibleArticles.length}</span> of{" "}
              <span style={{ color: c.textStrong2 }}>{filtered.length}</span> {filtered.length === 1 ? "article" : "articles"}
              {search && <> for "<span style={{ color: c.text85 }}>{search}</span>"</>}
            </p>
            {hasFilters && (
              <button onClick={clearFilters}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors hover:bg-white/5"
                style={{ color: c.text70 }}>
                <X size={12} /> Clear all
              </button>
            )}
          </div>
        </section>

        {/* Article Grid */}
        <section className="container-narrow px-4 pb-20 md:px-8 lg:px-16">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-4 rounded-xl border py-16 text-center"
              style={{ background: c.surfaceAlt, borderColor: c.borderSoft }}>
              <Search size={28} style={{ color: c.text40 }} />
              <div>
                <p className="text-base font-medium" style={{ color: c.text85 }}>No articles match your filters</p>
                <p className="mt-1 text-sm" style={{ color: c.text60 }}>
                  Try removing a tag or category to widen results.
                </p>
              </div>
              <button onClick={clearFilters}
                className="mt-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                style={{ background: "hsl(207 68% 50%)", color: "white" }}>
                Reset filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {visibleArticles.map((article) => {
                  const hue = accent(article.category);
                  return (
                    <Link key={article.slug} to={`/blog/${article.slug}`}
                      className="group relative flex flex-col overflow-hidden rounded-xl border transition-all duration-300 hover:-translate-y-0.5 hover:border-[hsl(var(--card-hue)_68%_50%/0.5)]"
                      style={{
                        background: c.surface,
                        borderColor: c.border,
                        ["--card-hue" as any]: hue,
                      } as React.CSSProperties}>
                      <span className="absolute left-0 top-0 h-full w-[3px]"
                        style={{ background: `linear-gradient(to bottom, hsl(${hue} 68% 55%), hsl(${hue} 68% 45% / 0.3))` }}
                        aria-hidden="true" />
                      <div className="flex flex-1 flex-col p-5 pl-6">
                        <span className="mb-3 inline-block w-fit rounded-full px-3 py-1 text-xs font-medium"
                          style={{ background: `hsl(${hue} 68% 50% / 0.15)`, color: `hsl(${hue} 68% 72%)` }}>
                          {article.category}
                        </span>
                        <h3 className="mb-2 text-lg font-semibold leading-snug transition-colors group-hover:text-white" style={{ color: c.textStrong }}>
                          {article.title}
                        </h3>
                        <p className="mb-3 flex-1 text-sm leading-relaxed line-clamp-3" style={{ color: c.text68 }}>
                          {article.excerpt}
                        </p>
                        {article.tags && article.tags.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-1">
                            {article.tags.slice(0, 3).map((t) => {
                              const key = normalizeTag(t);
                              const isActive = activeTags.has(key);
                              return (
                                <button
                                  key={key}
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleTag(key); }}
                                  className="rounded px-1.5 py-0.5 text-[10px] transition-colors"
                                  style={{
                                    background: isActive ? "hsl(207 68% 50% / 0.2)" : c.chipBg,
                                    color: isActive ? "hsl(207 68% 78%)" : c.text60,
                                    border: `1px solid ${isActive ? "hsl(207 68% 50% / 0.4)" : c.border}`,
                                  }}>
                                  #{t}
                                </button>
                              );
                            })}
                            {article.tags.length > 3 && (
                              <span className="text-[10px]" style={{ color: c.text45 }}>
                                +{article.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-3 text-xs" style={{ color: c.text60 }}>
                          <span>{article.author}</span>
                          <span>·</span>
                          <span>{new Date(article.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                          <span>·</span>
                          <span className="flex items-center gap-1"><Clock size={12} /> {article.readingTime} min</span>
                        </div>
                        <div className="mt-4 flex items-center gap-1.5 text-sm font-medium transition-all group-hover:gap-2.5"
                          style={{ color: `hsl(${hue} 68% 65%)` }}
                          aria-label={`Read more: ${article.title}`}>
                          Read article <ArrowRight size={14} aria-hidden="true" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {visible < filtered.length && (
                <div className="mt-10 flex justify-center">
                  <button onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="rounded-lg border px-6 py-2.5 text-sm font-medium transition-colors hover:bg-white/5"
                    style={{ background: c.surface, borderColor: c.borderStrong, color: c.textStrong2 }}>
                    Load more articles ({filtered.length - visible} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
};

export default Blog;
